import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { find, map, sortBy } from 'lodash';

import { claims } from 'configs/claims';
import { ProductTypes } from 'consts/productTypes/productTypes';
import { getAccommodationsMainNavigation } from 'helper/navigation/accommodations/main';
import { getLiteMainNavigation } from 'helper/navigation/lite/lite';
import { getToursMainNavigation } from 'helper/navigation/tours/main';
import { loadLanguage } from 'locales/i18n';

export const SettingsNavigationState = createContext();

export const useSettingsNavigationState = () => useContext(SettingsNavigationState);

const SettingsNavigationProvider = ({ children, profileInfo }) => {
  let { pathname } = useLocation();
  const { i18n } = useTranslation();

  const isRenter = profileInfo?.userType === 'renter';
  const hasCMDBFields = profileInfo?.hasCMDBFields;
  const isAdmin = profileInfo?.roles.includes('Administrator');
  const rentisticUserId = profileInfo?.rentisticUserId;
  const hasAnyProperty = profileInfo?.hasObjects;
  const fullName = `${profileInfo?.firstName} ${profileInfo?.lastName}`;
  const userEmail = profileInfo?.email;
  const customerName = profileInfo?.customerName;

  const customerFeaturesClaims = profileInfo?.customerFeatures?.map(item => item.name);
  const isPartnerBenefitsVisible =
    customerFeaturesClaims?.includes(claims.ViewPartnerBenefits) ||
    customerFeaturesClaims?.includes(claims.PartnerBenefits);

  useEffect(() => {
    if (profileInfo?.languageCode && i18n.language !== profileInfo?.languageCode) {
      loadLanguage(profileInfo?.languageCode).then(() =>
        i18n.changeLanguage(profileInfo?.languageCode),
      );
    }
  }, [profileInfo?.languageCode]);

  const getUserClaims = () => {
    return profileInfo?.claims.map(claim => claim.name);
  };
  const [userClaims, setUserClaims] = useState(getUserClaims());

  let accommodationsMainNavigation = getAccommodationsMainNavigation(userClaims);

  let liteMainNavigation = getLiteMainNavigation({
    isNewUser: !hasAnyProperty,
    hasCMDBFields,
    isRenter,
    userEmail,
    isPartnerBenefitsVisible,
  });

  let toursMainNavigation = getToursMainNavigation();

  const options = sortBy(
    map(profileInfo?.products, val => ({ value: val.code, label: val.name, code: val.code })),
    'value',
  );
  const isOnePackage = options.length === 1;
  const toursPackageExist = find(options, { code: ProductTypes.IZL });
  const rentalsPackageExist = find(options, { code: ProductTypes.SMJ });
  const isToursPath = pathname.split('/')[1] === 'tours';

  // header dropdown
  const getDefaultDropdown = () => {
    if (isOnePackage) return options[0];
    else if (isToursPath) return toursPackageExist;
    else return rentalsPackageExist;
  };
  const [headerDropdownValue, setHeaderDropdownValue] = useState(getDefaultDropdown());

  // main navigation
  const getDefaultNavigation = () => {
    if (isRenter) return liteMainNavigation;
    if (isOnePackage && rentalsPackageExist) return accommodationsMainNavigation;
    else if (isOnePackage && toursPackageExist) return toursMainNavigation;
    else if (isToursPath) return toursMainNavigation;
    else return accommodationsMainNavigation;
  };
  const [navigationConfig, setMainNavigationConfig] = useState(getDefaultNavigation());

  useEffect(() => {
    if (!isRenter) return;

    setMainNavigationConfig(
      getLiteMainNavigation({
        isNewUser: !hasAnyProperty,
        hasCMDBFields,
        isRenter,
        userEmail,
        isPartnerBenefitsVisible,
      }),
    );
  }, [hasAnyProperty, hasCMDBFields, isPartnerBenefitsVisible, isRenter, userEmail]);

  // mobile states
  const [expanded, setExpanded] = useState(true);
  const [mobileNavigationState, setMobileNavigationState] = useState(false);

  // submenu navigation - does not need to be opened by default
  const [submenuExpanded, setSubmenuExpanded] = useState(false);

  // reset navigation menu. used after language is changed in settings
  // TODO: rethink this logic
  const resetDefaultNavigation = () => {
    accommodationsMainNavigation = getAccommodationsMainNavigation(userClaims);
    toursMainNavigation = getToursMainNavigation();
    liteMainNavigation = getLiteMainNavigation({ isNewUser: !hasAnyProperty });
    setMainNavigationConfig(getDefaultNavigation());
  };

  const value = {
    state: {
      userClaims,
      navigationConfig,
      expanded,
      mobileNavigationState,
      submenuExpanded,
      headerDropdownValue,
      options,
      isRenter,
      isAdmin,
      rentisticUserId,
      hasAnyProperty,
      fullName,
      isOnePackage,
      customerName,
    },
    setMainNavigationConfig,
    setExpanded,
    setMobileNavigationState,
    setSubmenuExpanded,
    setHeaderDropdownValue,
    setUserClaims,
    resetDefaultNavigation,
  };

  return (
    <SettingsNavigationState.Provider value={value}>{children}</SettingsNavigationState.Provider>
  );
};

export default SettingsNavigationProvider;
